<template>
  <div class="bulk-assignment-selector">
    <div class="mb-6">
      <h4 class="text-base font-semibold text-slate-900 dark:text-white mb-2">
        {{ $t('BULK_BROADCAST.ASSIGNMENT.TITLE') }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('BULK_BROADCAST.ASSIGNMENT.DESCRIPTION') }}
      </p>
    </div>

    <!-- Assignment Type Selection -->
    <div class="space-y-6">
      <!-- Team Assignment -->
      <div class="assignment-section">
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          <Icon icon="i-lucide-users" class="inline mr-2" size="16" />
          {{ $t('BULK_BROADCAST.ASSIGNMENT.ASSIGN_TEAM') }}
        </label>
        
        <div class="relative">
          <select
            v-model="selectedTeamId"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            @change="handleTeamChange"
          >
            <option :value="'keep_current'">
              {{ $t('BULK_BROADCAST.ASSIGNMENT.KEEP_CURRENT_TEAM') }}
            </option>
            <option :value="null">
              {{ $t('BULK_BROADCAST.ASSIGNMENT.NO_TEAM') }}
            </option>
            <option
              v-for="team in teams"
              :key="team.id"
              :value="team.id"
            >
              {{ team.name }}
            </option>
          </select>
        </div>
        
        <!-- Explanation for keep current -->
        <p v-if="selectedTeamId === 'keep_current'" class="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('BULK_BROADCAST.ASSIGNMENT.KEEP_CURRENT_TEAM_HELP') }}
        </p>
        
        <!-- Explanation for none -->
        <p v-else-if="selectedTeamId === null" class="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('BULK_BROADCAST.ASSIGNMENT.NO_TEAM_HELP') }}
        </p>
      </div>

      <!-- Agent Assignment -->
      <div class="assignment-section">
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          <Icon icon="i-lucide-user" class="inline mr-2" size="16" />
          {{ $t('BULK_BROADCAST.ASSIGNMENT.ASSIGN_AGENT') }}
        </label>

        <div class="relative">
          <select
            v-model="selectedAgentId"
            :disabled="agentsLoading"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            @change="handleAgentChange"
          >
            <option :value="'keep_current'">
              {{ $t('BULK_BROADCAST.ASSIGNMENT.KEEP_CURRENT_AGENT') }}
            </option>
            <option :value="null">
              {{ $t('BULK_BROADCAST.ASSIGNMENT.NO_AGENT') }}
            </option>
            <option
              v-for="agent in availableAgents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>

          <div
            v-if="agentsLoading"
            class="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <Icon icon="i-lucide-loader-2" class="animate-spin text-slate-400" size="16" />
          </div>
        </div>
        
        <!-- Explanation for keep current -->
        <p v-if="selectedAgentId === 'keep_current'" class="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('BULK_BROADCAST.ASSIGNMENT.KEEP_CURRENT_AGENT_HELP') }}
        </p>
        
        <!-- Explanation for none -->
        <p v-else-if="selectedAgentId === null" class="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('BULK_BROADCAST.ASSIGNMENT.NO_AGENT_HELP') }}
        </p>

        <!-- Warning if both team and agent selected -->
        <div
          v-if="isNumericId(selectedTeamId) && isNumericId(selectedAgentId) && !isAgentInTeam"
          class="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
        >
          <div class="flex items-start space-x-2">
            <Icon icon="i-lucide-alert-triangle" class="text-amber-600 dark:text-amber-400 mt-0.5" size="16" />
            <p class="flex-1 text-sm text-amber-700 dark:text-amber-300">
              {{ $t('BULK_BROADCAST.ASSIGNMENT.AGENT_NOT_IN_TEAM') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div
        v-if="hasSummary"
        class="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <h5 class="text-sm font-medium text-slate-900 dark:text-white mb-2">
          {{ $t('BULK_BROADCAST.ASSIGNMENT.SUMMARY') }}
        </h5>
        <ul class="space-y-1 text-sm text-slate-600 dark:text-slate-400">
          <li v-if="selectedTeamId === 'keep_current'" class="flex items-center space-x-2">
            <Icon icon="i-lucide-info" class="text-blue-600" size="14" />
            <span>{{ $t('BULK_BROADCAST.ASSIGNMENT.TEAM') }}: <strong>{{ $t('BULK_BROADCAST.ASSIGNMENT.WILL_KEEP_CURRENT') }}</strong></span>
          </li>
          <li v-else-if="selectedTeamId === null" class="flex items-center space-x-2">
            <Icon icon="i-lucide-x" class="text-red-600" size="14" />
            <span>{{ $t('BULK_BROADCAST.ASSIGNMENT.TEAM') }}: <strong>{{ $t('BULK_BROADCAST.ASSIGNMENT.WILL_REMOVE') }}</strong></span>
          </li>
          <li v-else-if="selectedTeam" class="flex items-center space-x-2">
            <Icon icon="i-lucide-check" class="text-green-600" size="14" />
            <span>{{ $t('BULK_BROADCAST.ASSIGNMENT.TEAM') }}: <strong>{{ selectedTeam.name }}</strong></span>
          </li>
          
          <li v-if="selectedAgentId === 'keep_current'" class="flex items-center space-x-2">
            <Icon icon="i-lucide-info" class="text-blue-600" size="14" />
            <span>{{ $t('BULK_BROADCAST.ASSIGNMENT.AGENT') }}: <strong>{{ $t('BULK_BROADCAST.ASSIGNMENT.WILL_KEEP_CURRENT') }}</strong></span>
          </li>
          <li v-else-if="selectedAgentId === null" class="flex items-center space-x-2">
            <Icon icon="i-lucide-x" class="text-red-600" size="14" />
            <span>{{ $t('BULK_BROADCAST.ASSIGNMENT.AGENT') }}: <strong>{{ $t('BULK_BROADCAST.ASSIGNMENT.WILL_REMOVE') }}</strong></span>
          </li>
          <li v-else-if="selectedAgent" class="flex items-center space-x-2">
            <Icon icon="i-lucide-check" class="text-green-600" size="14" />
            <span>{{ $t('BULK_BROADCAST.ASSIGNMENT.AGENT') }}: <strong>{{ selectedAgent.name }}</strong></span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ teamId: 'keep_current', agentId: 'keep_current' })
  },
  selectedInbox: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();

// State
const selectedTeamId = ref(props.modelValue?.teamId || 'keep_current');
const selectedAgentId = ref(props.modelValue?.agentId || 'keep_current');
const agentsLoading = ref(false);

// Getters
const teams = useMapGetter('teams/getTeams');
const inboxOnlyMembers = useMapGetter('inboxOnlyMembers/getInboxOnlyMembers');

// Computed
const selectedTeam = computed(() => {
  if (!isNumericId(selectedTeamId.value)) return null;
  return teams.value.find(t => t.id === selectedTeamId.value);
});

const inboxId = computed(() => props.selectedInbox?.id);

// Helper to check if value is a numeric ID
const isNumericId = (value) => {
  return value !== null && value !== 'keep_current' && typeof value === 'number';
};

// Agentes disponibles según inbox y team seleccionado
const availableAgents = computed(() => {
  if (!inboxId.value) return [];
  
  // Si hay equipo numérico seleccionado, filtrar por ese equipo
  const teamFilter = isNumericId(selectedTeamId.value) ? selectedTeamId.value : null;
  
  // Usar el mismo getter que usa ConversationAction
  const members = inboxOnlyMembers.value(inboxId.value, teamFilter);
  return members || [];
});

const selectedAgent = computed(() => {
  if (!isNumericId(selectedAgentId.value)) return null;
  return availableAgents.value.find(a => a.id === selectedAgentId.value);
});

const isAgentInTeam = computed(() => {
  if (!isNumericId(selectedTeamId.value) || !isNumericId(selectedAgentId.value)) {
    return true;
  }
  
  // Verificar si el agente está en la lista de agentes disponibles del equipo
  return availableAgents.value.some(agent => agent.id === selectedAgentId.value);
});

// Check if summary should be shown
const hasSummary = computed(() => {
  return selectedTeamId.value !== 'keep_current' || selectedAgentId.value !== 'keep_current';
});

// Methods
const emitUpdate = () => {
  emit('update:modelValue', {
    teamId: selectedTeamId.value,
    agentId: selectedAgentId.value
  });
};

const handleTeamChange = async () => {
  // Recargar agentes cuando cambia el equipo
  await loadInboxMembers();
  
  // Si el agente actual no está en el nuevo equipo, limpiarlo
  if (isNumericId(selectedAgentId.value) && !isAgentInTeam.value) {
    selectedAgentId.value = 'keep_current';
  }
  emitUpdate();
};

const handleAgentChange = () => {
  emitUpdate();
};

const loadInboxMembers = async () => {
  if (!inboxId.value) return;
  
  agentsLoading.value = true;
  try {
    // Cargar miembros del inbox, opcionalmente filtrados por equipo
    const teamFilter = isNumericId(selectedTeamId.value) ? selectedTeamId.value : null;
    await store.dispatch('inboxOnlyMembers/fetch', {
      inboxIds: [inboxId.value],
      teamId: teamFilter
    });
  } catch (error) {
    // Error al cargar miembros
  } finally {
    agentsLoading.value = false;
  }
};

const loadData = async () => {
  try {
    // Cargar equipos
    await store.dispatch('teams/get');
    
    // Cargar miembros del inbox
    await loadInboxMembers();
  } catch (error) {
    // Error al cargar datos
  }
};

// Lifecycle
onMounted(() => {
  loadData();
});

// Watch for inbox changes
watch(() => props.selectedInbox, () => {
  // Reset selections when inbox changes
  selectedTeamId.value = 'keep_current';
  selectedAgentId.value = 'keep_current';
  emitUpdate();
  loadData();
});
</script>

<style scoped>
.bulk-assignment-selector {
  max-width: 600px;
}

.assignment-section {
  /* Additional styling if needed */
}
</style>
